// Живой мониторинг промежуточных дел: держит открытым /ws/intermediate
// (per-user канал, как /ws и /ws/chats) и применяет события к стору.
// Буферизации на реконнекте нет — при каждом (пере)подключении перечитываем
// GET /tasks/intermediate, чтобы получить актуальное состояние.
import { useEffect } from 'react';
import { toast } from 'sonner';

import { env } from '@/shared/config';

import { listIntermediate } from './api';
import type { IntermediateState, IntermediateTick } from './api';
import { useIntermediateStore } from './store';
import { readCookie } from '@/shared/lib/readCookie';

async function refreshList() {
  try {
    const items = await listIntermediate();
    useIntermediateStore.getState().setAll(items);
  } catch (e) {
    console.warn('listIntermediate failed', e);
  }
}

export function useIntermediateSocket() {
  useEffect(() => {
    let cancelled = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;
    let ws: WebSocket | null = null;

    function connect() {
      if (cancelled) {
        return;
      }
      const token = readCookie('jwt_token');
      if (!token) {
        // Ещё не авторизованы — подождём куку, не считая это сбоем.
        reconnectTimer = setTimeout(connect, 3000);
        return;
      }

      // Синхронизация состояния при каждом подключении (и реконнекте).
      void refreshList();

      const httpBase = env.apiBase || window.location.origin;
      const wsBase = httpBase.replace(/^http/, 'ws');
      const url = `${wsBase}/${env.apiPrefix}/ws/intermediate?token=${encodeURIComponent(token)}`;
      try {
        ws = new WebSocket(url);
      } catch {
        scheduleReconnect();
        return;
      }

      ws.onopen = () => {
        attempts = 0;
      };
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const store = useIntermediateStore.getState();
          switch (data?.type) {
            case 'intermediate_timer_tick':
              store.applyTicks((data.payload ?? []) as IntermediateTick[]);
              break;
            case 'intermediate_timer_state_changed':
              if (data.payload) {
                store.upsert(data.payload as IntermediateState);
              }
              break;
            case 'intermediate_timer_error': {
              const taskId = data.payload?.taskId;
              // Время не потеряно (копится в pendingSeconds), но пользователь
              // должен видеть, что запись в Bitrix не прошла.
              toast.warning(
                `Не удалось записать время дела${taskId ? ` #${taskId}` : ''} в Bitrix`,
                {
                  description:
                    'Время не потеряно — отправим при следующей паузе или завершении',
                }
              );
              break;
            }
            default:
              break;
          }
        } catch {
          /* не-JSON — игнор */
        }
      };
      ws.onclose = () => {
        scheduleReconnect();
      };
      ws.onerror = () => {
        try {
          ws?.close();
        } catch {
          /* noop */
        }
      };
    }

    function scheduleReconnect() {
      if (cancelled) {
        return;
      }
      attempts += 1;
      const delay = Math.min(30_000, 1000 * 2 ** Math.min(attempts, 5));
      reconnectTimer = setTimeout(connect, delay);
    }

    connect();
    return () => {
      cancelled = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      try {
        ws?.close();
      } catch {
        /* noop */
      }
    };
  }, []);
}
